import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const DialogTagsAdmin = ({ open, handleClose, startupData, onSave, innovationId }) => {
  const [categories, setCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) return console.error("API URL is not defined.");

        // Se innovationId è falsy, non fare la fetch
        if (!innovationId) {
          const taxonomyRes = await axios.get(`${apiUrl}/taxonomies?type=tag`);
          const taxonomyData = taxonomyRes.data.data;
        
          setCategories(taxonomyData);
          setSelectedTags([]); // assicurati di azzerare i tag selezionati
          return;
        }

        const [taxonomyRes, innovationRes] = await Promise.all([
          axios.get(`${apiUrl}/taxonomies?type=tag`),
          axios.get(`${apiUrl}/innovations/${innovationId}?admin=true`),
        ]);

        const taxonomyData = taxonomyRes.data.data;
        const innovationTags = innovationRes.data.data?.taxonomies?.tags || [];

        setCategories(taxonomyData);
        setSelectedTags(innovationTags);
      } catch (err) {
        console.error("Error fetching tags:", err);
      }
    };

    fetchData();
  }, [innovationId]);

  const handleTagSelect = (tag) => {
    if (!selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleTagRemove = (tagId) => {
    setSelectedTags(selectedTags.filter((tag) => tag.id !== tagId));
  };

  const handleSave = async () => {
    if (!innovationId) {
      onSave(selectedTags);
      handleClose();
      return;
    }

    try {
      const updatedTags = selectedTags.map((tag) => Number(tag.id));
      const payload = { tags: updatedTags };
      const apiUrl = process.env.REACT_APP_API_URL;
      await axios.patch(`${apiUrl}/innovations/${innovationId}`, payload);
      handleClose();
    } catch (err) {
      console.error("Error saving tags:", err);
    }
  };

  const handleCreateTag = async (tagName) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await axios.post(`${apiUrl}/taxonomies/tags`, { name: tagName });
      const newTag = res.data;
  
      if (newTag && newTag.name) {
        setCategories([...categories, newTag]);
        setSelectedTags([...selectedTags, newTag]);
        setSearchTerm(""); // pulisci input
      } else {
        console.error("Invalid tag format returned from API:", newTag);
      }
    } catch (err) {
      console.error("Error creating tag:", err);
    }
  };
  

  const filteredCategories = categories.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showCreateSuggestion =
    searchTerm.length > 0 &&
    !categories.some((tag) => tag.name.toLowerCase() === searchTerm.toLowerCase());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          backgroundColor: "#191919",
          color: "white",
          fontFamily: "Urbanist",
        }}
      >
        Associated Tags
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#191919", color: "white" }}>
        {/* Selected Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedTags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              onDelete={() => handleTagRemove(tag.id)}
              deleteIcon={
                <span
                  style={{
                    color: "rgb(var(--global-color-primary))",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  &times;
                </span>
              }
              sx={{
                backgroundColor: "transparent",
                border: "1.5px solid rgb(var(--global-color-primary))",
                borderRadius: "5px",
                fontFamily: "Urbanist",
                color: "rgb(var(--global-color-primary))",
                fontWeight: "bold",
              }}
            />
          ))}
        </div>

        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search or create a new tag..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            mb: 2,
            input: { color: "white", fontFamily: "Urbanist" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "gray" },
              "&:hover fieldset": { borderColor: "white" },
              "&.Mui-focused fieldset": { borderColor: "rgb(var(--global-color-primary))" },
            },
          }}
        />

        {/* Chips */}
        <div className="flex flex-wrap gap-2">
          
          {filteredCategories.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              onClick={() => handleTagSelect(tag)}
              disabled={selectedTags.some((t) => t.id === tag.id)}
              sx={{
                backgroundColor: selectedTags.some((t) => t.id === tag.id)
                  ? "rgba(255,255,255,0.3)"
                  : "white",
                fontFamily: "Urbanist",
                color: selectedTags.some((t) => t.id === tag.id) ? "gray" : "black",
                fontWeight: "bold",
                cursor: selectedTags.some((t) => t.id === tag.id) ? "not-allowed" : "pointer",
                "&:hover": {
                  backgroundColor: selectedTags.some((t) => t.id === tag.id)
                    ? "rgba(255,255,255,0.3)"
                    : "rgb(var(--global-color-primary))",
                  color: "black",
                },
              }}
            />
          ))}
        </div>

        {/* Create New Tag Suggestion */}
        {showCreateSuggestion && (
          <Typography
            onClick={() => handleCreateTag(searchTerm)}
            sx={{
              mt: 2,
              color: "rgb(var(--global-color-primary))",
              cursor: "pointer",
              fontFamily: "Urbanist",
              fontWeight: "bold",
              textDecoration: "underline",
            }}
          >
            Do you want to create the tag "{searchTerm}"?
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#191919" }}>
        <Button
          onClick={handleClose}
          sx={{
            backgroundColor: "gray",
            color: "white",
            "&:hover": { backgroundColor: "rgba(128, 128, 128, 0.8)" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            backgroundColor: "rgb(var(--global-color-primary))",
            color: "black",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "rgba(var(--global-color-primary), 0.8)" },
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogTagsAdmin;
